import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { Button, Form } from 'react-bootstrap';

import { jwtDecode } from 'jwt-decode';
import { useDispatch, useSelector } from 'react-redux';

import fleetBrand from '../../assets/images/logos/FleetWhite.png';

import { ROLE_ENUM } from '../../enums/role-enum.enum';

import {
  faExclamationCircle,
  faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import useGetValidateRegistrationToken from '../../api/auth/useGetValidateRegistrationToken';
import usePostSetupPassword from '../../api/auth/usePostSetupPassword';
import { setConfiguration, setFeatures } from '../../reducer/actions';
import './login.scss';
import RegisterInviteModal from './register-invite-modal';

export default function Login() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const REGISTRATION_TOKEN = new URLSearchParams(location?.search).get(
    'invited_user'
  );

  const [isOpenRegisterInvite, setIsOpenRegisterInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [errorStatus, setErrorStatus] = useState(false);

  const BASE_URL = useSelector((state) => state.BASE_URL);

  const { data, getValidateRegistrationToken, isSuccess } =
    useGetValidateRegistrationToken();

  const {
    isLoading,
    setupPassword,
    isSuccess: isSuccessSetupPassword,
  } = usePostSetupPassword();

  useEffect(() => {
    if (REGISTRATION_TOKEN) {
      getValidateRegistrationToken(REGISTRATION_TOKEN);
    }
  }, [REGISTRATION_TOKEN]);

  useEffect(() => {
    if (isSuccess) {
      setIsOpenRegisterInvite(true);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccessSetupPassword) {
      setIsOpenRegisterInvite(false);
      setEmail(data?.tokenData?.email);
    }
  }, [isSuccessSetupPassword]);


  const handleSuccessfulLogin = (token, clientId, decoded) => {
    localStorage.setItem('fleetToken', token);
    localStorage.setItem('fleetClientId', clientId);

    dispatch(
      setConfiguration({
        is_admin: decoded?.user?.roles?.includes(ROLE_ENUM.ADMIN),
        is_super_admin: decoded?.user?.roles?.includes(ROLE_ENUM.SUPER_ADMIN),
        logo_url: decoded?.client?.logo_url,
        client_id: decoded?.user?.client_id,
      })
    );
    dispatch(setFeatures(decoded.client.features));

    // Redirection après que tout soit bien configuré
    window.location.href = '/';
  };

  const login = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const result = await response.json();

      if (response.ok && result.token) {
        const decoded = result.token ? jwtDecode(result.token) : null;
        if (decoded) {
          handleSuccessfulLogin(result.token, result.clientId, decoded);
        }
        setEmail('');
        setPassword('');
      } else if (response.status === 401 || response.status === 404) {
        setErrorMessage(result?.message || 'Mot de passe ou e-mail incorrect.');
        setErrorStatus(true);
      } else if (response.status === 400) {
        setErrorMessage("Une erreur s'est produite");
        setErrorStatus(true);
      } else {
        setErrorMessage(
          result?.message ||
            'Une erreur réseau est survenue, veuillez contacter le support client.'
        );
        setErrorStatus(true);
      }
    } catch (error) {
      console.error("Il y a eu un problème avec l'opération fetch:", error);
      setErrorMessage('Une erreur réseau est survenue, veuillez réessayer.');
      setErrorStatus(true);
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
  };

  // Vérification de l'auth au montage du composant
  useEffect(() => {
    const token = localStorage.getItem('fleetToken');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        navigate('/');
      } catch (error) {
        // Token invalide, on le supprime
        localStorage.removeItem('fleetToken');
        localStorage.removeItem('fleetClientId');
      }
    }
  }, [navigate]);

  return (
    <>
      <div className="login-container">
        {/* Background image with trucks on road */}
        <div className="background-image"></div>

        {/* Login form */}
        <div className="login-content">
          <div className="login-card">
            {/* Logo */}
            <div className="logo-container">
              <img src={fleetBrand} alt="Fleet Logo" className="logo" />
            </div>

            {/* Welcome message */}
            <h1 className="welcome-title">Bienvenue</h1>
            <p className="welcome-subtitle">Connectez-vous à votre plateforme Fleet</p>

            {/* Error message */}
            {errorStatus && (
              <div className="error-alert">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{errorMessage}</span>
              </div>
            )}

            {/* Login form */}
            <Form className="login-form" onSubmit={login}>
              <Form.Group className="form-group">
                <Form.Label>Adresse email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="vous@exemple.com"
                  autoComplete="username"
                  onChange={handleEmailChange}
                  value={email}
                  className="form-input"
                />
              </Form.Group>

              <Form.Group className="form-group">
                <div className="label-with-link">
                  <Form.Label>Mot de passe</Form.Label>
                  <Link to="/forgot" className="forgot-link">
                    Oublié ?
                  </Link>
                </div>
                <Form.Control
                  type="password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  onChange={handlePasswordChange}
                  value={password}
                  className="form-input"
                />
              </Form.Group>

                  <Button
                    type="submit"
                    disabled={email === '' || password === ''}
                className="submit-btn"
                  >
                Se connecter
                <FontAwesomeIcon icon={faArrowRight} className="btn-icon" />
                  </Button>
              </Form>

            {/* Footer links */}
            <div className="login-footer">
              <p className="new-client">
                Nouveau client ?{' '}
                <a href="https://www.agrivitech.com/contact" target="_blank" rel="noopener noreferrer">
                  Demander un accès
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <RegisterInviteModal
        isOpen={isOpenRegisterInvite}
        setIsOpen={setIsOpenRegisterInvite}
        invitedUser={data?.tokenData}
        token={REGISTRATION_TOKEN}
        isLoading={isLoading}
        setupPassword={setupPassword}
      />
    </>
  );
}

const checkAuth = () => {
  return localStorage.getItem('fleetToken');
};
